/**
 * Client workspace · Strategy tab — wired against
 * `list-client-strategies?client_id=`, `get-client-strategy?strategy_id=`,
 * and `list-strategy-suggestions?strategy_id=`.
 *
 * Per `docs/frontend/035-frontend-app-shell-plan.md §6.2`:
 *   - 404 from any endpoint = permission-denied / cross-org probe (RLS hides);
 *   - empty arrays = legitimate "not authored yet";
 *   - any other 4xx/5xx = error state.
 *
 * Suggestions are agency-only per the strategy_suggestions RLS policy
 * (Task 010 §4.3). Client users will see the parent strategy but receive
 * an empty suggestions list — that's the correct behaviour, surfaced as
 * an empty-state panel rather than an error.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isApiError } from '../../api/client';
// Task 245 — the ONLY direct `supabase.rpc(...)` call in the app (Option B). See
// `CreateContentBatchButton` for why this localized deviation from the kmiFetch→
// edge-function convention is intentional and must NOT be generalized.
import { supabase } from '../../auth/supabase';
import {
  createClientStrategyFromOnboarding,
  getClientOnboardingProfile,
  getClientOverview,
  getClientStrategy,
  listClientStrategies,
  listStrategySuggestions,
} from '../../api/endpoints';
import { useMeContext } from '../../context/MeContextProvider';
import { hasAnyPermission, isLeadership, PERM } from '../../permissions';
import { StrategyEditor } from './StrategyEditor';
import { StrategyRulesPanel } from './StrategyRulesPanel';
import { isStrategyReadinessFieldKey, readinessFieldLabel } from './strategyReadiness';
import { useStrategyGenerationJob } from './useStrategyGenerationJob';
import {
  hasAnyV1Section,
  normalizeStrategyContent,
  type ChannelRecommendationItem,
  type CompetitorSummaryItem,
  type ContentMixEntry,
  type ContentPillarItem,
  type DosDontsSection,
  type KpiTargetItem,
  type PersonaItem,
  type PositioningSection,
  type RoadmapItem,
  type StrategyContentV1Render,
  type ToneGuidelinesSection,
  type ValuePropositionSection,
} from '../../api/strategyContent';
import type {
  ClientOnboardingProfile,
  ClientOverviewData,
  ClientStrategyRow,
  GetClientStrategyData,
  StrategySuggestionRow,
} from '../../api/types';
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '../../components/states';

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly strategies: ReadonlyArray<ClientStrategyRow> }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

type DetailState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly detail: GetClientStrategyData;
      readonly suggestions: ReadonlyArray<StrategySuggestionRow>;
      readonly suggestionsError: unknown | null;
    }
  | { readonly status: 'not-found' }
  | { readonly status: 'error'; readonly error: unknown };

interface ReadinessSnapshot {
  readonly hasBrandIdentity: boolean;
  readonly hasOnboardingProfile: boolean;
  readonly audienceCount: number;
  readonly goalCount: number;
  readonly clientStatus: ClientOverviewData['client']['status'] | null;
}

export function StrategyPage() {
  const { clientId } = useParams();
  const { me } = useMeContext();
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);
  // Bumped after `publish-client-strategy-version` to force the
  // detail effect to re-fetch the new active version. Independent of
  // `reloadKey` so editing doesn't re-fetch the strategy LIST too.
  const [detailReloadKey, setDetailReloadKey] = useState(0);
  const [editing, setEditing] = useState(false);
  // Mirror of the perm logic in `StrategyEmptyState` — same key set,
  // since publishing a new version uses the same permission as
  // creating the baseline (`strategy.version.publish`). The backend
  // re-checks regardless.
  const canPublishVersion =
    !!me && !me.system_admin && hasAnyPermission(me.permissions, PERM.STRATEGY_VERSION_PUBLISH);
  // Task 061 — AI generation needs `ai.run` plus the strategy
  // permissions enforced by the publish RPC. Without `ai.run`, the
  // backend correctly 403s; gating the button keeps users out of
  // the active-button → 403 round-trip.
  const canAiRun = !!me && !me.system_admin && hasAnyPermission(me.permissions, PERM.AI_RUN);
  // Task 245 — "Create Content Batch" CTA gate. V1 reuses `content.create` ONLY
  // (owner / marketing_manager hold it by default; no new permission is minted and
  // none is granted to other roles). The backend RPC re-checks; gating here keeps
  // non-content.create users out of the active-button → 403 round-trip.
  const canCreateContentBatch =
    !!me && !me.system_admin && hasAnyPermission(me.permissions, PERM.CONTENT_CREATE);
  // Task 251 — Strategy Rules review surface. VIEW is gated on
  // `strategy.rule.review` (owner / marketing_manager / media_buyer hold it).
  // Task 292C3A extends VIEW to `strategy.read.assigned_active`
  // (content_writer / senior_content_writer) so eligible writers see
  // the READ-ONLY rules list on the StrategyPage's own render (variant
  // `readonly`, `canManage=false`, `canDerive=false`). The manage link
  // and the derive/approve/dismiss controls both depend on
  // `canManageStrategyRules` (leadership only) so writers see the list
  // but no mutation surface; the RLS `strategy_rules_select` branch and
  // the strategy-rules mutation RPCs re-check regardless.
  const canViewStrategyRules =
    !!me &&
    !me.system_admin &&
    hasAnyPermission(me.permissions, PERM.STRATEGY_RULE_REVIEW, PERM.STRATEGY_READ_ASSIGNED_ACTIVE);
  const canManageStrategyRules = !!me && !me.system_admin && isLeadership(me);
  // Task 048: live readiness signals for the empty state. Loaded
  // alongside strategies so the "what's missing before generation"
  // panel is honest. Tolerates permission_denied / not_found per
  // signal — a partial readiness snapshot is better than blocking
  // the page on a missing tab.
  const [readiness, setReadiness] = useState<ReadinessSnapshot | null>(null);

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    setList({ status: 'loading' });
    listClientStrategies(clientId, { signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setList({ status: 'ready', strategies: data.strategies });
        // Auto-select first strategy, but discard a stale prior selection
        // that does not exist in the new list (e.g. after a client switch).
        if (data.strategies.length > 0 && data.strategies[0]) {
          setSelectedId((prev) =>
            prev && data.strategies.some((s) => s.id === prev) ? prev : data.strategies[0]!.id,
          );
        } else {
          setSelectedId(null);
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setList({ status: 'not-found' });
          return;
        }
        setList({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [clientId, reloadKey]);

  useEffect(() => {
    if (!clientId) return;
    const ctrl = new AbortController();
    Promise.all([
      getClientOverview(clientId, { signal: ctrl.signal }).catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') throw err;
        if (isApiError(err) && (err.code === 'not_found' || err.code === 'permission_denied')) {
          return null;
        }
        throw err;
      }),
      getClientOnboardingProfile(clientId, { signal: ctrl.signal }).catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') throw err;
        if (isApiError(err) && (err.code === 'not_found' || err.code === 'permission_denied')) {
          return { onboarding_profile: null as ClientOnboardingProfile | null };
        }
        throw err;
      }),
    ])
      .then(([overview, onboarding]) => {
        if (ctrl.signal.aborted) return;
        const profile = onboarding?.onboarding_profile ?? null;
        // "Onboarding profile filled" = at least one of the foundational
        // long-form fields is non-empty. Prevents an all-empty row from
        // counting as ready.
        // Mirrors the backend's `enforceReadiness` check in
        // `supabase/functions/create-client-strategy-from-onboarding/index.ts`:
        // a non-null `completed_at` ALONE is a sufficient signal even
        // if the long-form text fields are empty (the agency can mark
        // a profile complete after a workshop without copy-pasting
        // every section). Keeping the two in sync prevents the
        // frontend from showing "أكمل البيانات أولاً" while the
        // backend would happily generate.
        const hasOnboarding =
          !!profile &&
          (!!profile.completed_at ||
            truthy(profile.vision) ||
            truthy(profile.mission) ||
            truthy(profile.values_text) ||
            truthy(profile.usp) ||
            truthy(profile.arabic_description) ||
            truthy(profile.english_description));
        setReadiness({
          hasBrandIdentity: !!overview?.brand_info,
          hasOnboardingProfile: hasOnboarding,
          audienceCount: overview?.target_audiences.length ?? 0,
          goalCount: overview?.goals.length ?? 0,
          clientStatus: overview?.client.status ?? null,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Non-fatal: leave readiness null so the empty state shows
        // generic guidance without false-positive ✓ marks.
      });
    return () => ctrl.abort();
  }, [clientId, reloadKey]);

  useEffect(() => {
    if (!selectedId) {
      setDetail({ status: 'idle' });
      return;
    }
    const ctrl = new AbortController();
    setDetail({ status: 'loading' });
    Promise.all([
      getClientStrategy(selectedId, { signal: ctrl.signal }),
      listStrategySuggestions(selectedId, { signal: ctrl.signal }).then(
        (data) => ({ ok: true as const, data }),
        (err: unknown) => ({ ok: false as const, error: err }),
      ),
    ])
      .then(([strategyDetail, suggestionsResult]) => {
        if (ctrl.signal.aborted) return;
        if (suggestionsResult.ok) {
          setDetail({
            status: 'ready',
            detail: strategyDetail,
            suggestions: suggestionsResult.data.suggestions,
            suggestionsError: null,
          });
          return;
        }
        setDetail({
          status: 'ready',
          detail: strategyDetail,
          suggestions: [],
          suggestionsError: suggestionsResult.error,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setDetail({ status: 'not-found' });
          return;
        }
        setDetail({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [selectedId, detailReloadKey]);

  if (!clientId) return null;

  if (list.status === 'loading') return <LoadingState title="جاري تحميل الاستراتيجيات…" />;
  if (list.status === 'not-found')
    return (
      <NotFoundState
        title="لا يمكن الوصول إلى استراتيجيات هذا العميل"
        description="إما أن العميل غير موجود، أو لا تملك صلاحية فتح ملفه."
      />
    );
  if (list.status === 'error')
    return (
      <ErrorState
        title="تعذر تحميل الاستراتيجيات"
        error={list.error}
        onRetry={() => setReloadKey((n) => n + 1)}
      />
    );

  if (list.strategies.length === 0) {
    return (
      <StrategyEmptyState
        clientId={clientId}
        readiness={readiness}
        onCreated={() => setReloadKey((n) => n + 1)}
      />
    );
  }

  return (
    <div className="workspace-split">
      <aside className="workspace-list">
        <div className="sec-title">
          <h2>قائمة الاستراتيجيات</h2>
          <span className="meta">{list.strategies.length}</span>
        </div>
        <ul className="row-list">
          {list.strategies.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                className={s.id === selectedId ? 'row-card row-card-active' : 'row-card'}
                onClick={() => setSelectedId(s.id)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <strong style={{ fontSize: 13 }}>{s.title}</strong>
                  {s.is_baseline ? <span className="pill pill-info">أساسية</span> : null}
                </div>
                <div className="tiny" style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                  <span className={s.is_active ? 'pill pill-active' : 'pill pill-paused'}>
                    {s.is_active ? 'نشطة' : 'متوقفة'}
                  </span>
                  <span style={{ color: 'var(--muted)' }}>
                    {new Date(s.updated_at).toLocaleDateString('ar-SA')}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section className="workspace-detail">
        <StrategyDetail
          state={detail}
          editing={editing}
          canPublishVersion={canPublishVersion}
          canAiRun={canAiRun}
          canCreateContentBatch={canCreateContentBatch}
          canViewStrategyRules={canViewStrategyRules}
          canManageStrategyRules={canManageStrategyRules}
          onEdit={() => setEditing(true)}
          onCancelEdit={() => setEditing(false)}
          onSaved={() => {
            // Close the editor, then bump the detail reload so the
            // freshly-published version becomes the rendered active.
            setEditing(false);
            setDetailReloadKey((n) => n + 1);
          }}
        />
      </section>
    </div>
  );
}

function StrategyEmptyState({
  clientId,
  readiness,
  onCreated,
}: {
  readonly clientId: string;
  readonly readiness: ReadinessSnapshot | null;
  readonly onCreated: () => void;
}) {
  const { me } = useMeContext();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Task 085: async AI generation. The hook owns the start →
  // poll → terminal lifecycle; this component only renders the
  // current phase + wires the success callback to refresh the
  // strategies list.
  const aiJob = useStrategyGenerationJob({
    clientId,
    onSuccess: onCreated,
  });
  const aiSubmitting = aiJob.isBusy;
  // Gate the CTA on BOTH perms because the backend RPC writes both
  // tables (matches the policy in
  // docs/frontend/050-strategy-content-contract.md §5.2). Frontend
  // mismatch would just bounce off the backend anyway, but failing
  // closed at click-time gives a clean message instead of an opaque
  // permission_denied banner.
  const perms = me?.permissions ?? [];
  const canGenerate =
    !!me &&
    !me.system_admin &&
    hasAnyPermission(perms, PERM.STRATEGY_CREATE) &&
    hasAnyPermission(perms, PERM.STRATEGY_VERSION_PUBLISH);
  // Task 061 AI generation needs the strategy perms on the empty-
  // state path (creating a baseline calls the same SECURITY DEFINER
  // RPC inside `generate-client-strategy-ai`) PLUS `ai.run`. The
  // backend correctly 403s callers without ai.run; gating the button
  // here too prevents the active-button → 403 round-trip.
  const canAiGenerate = canGenerate && hasAnyPermission(perms, PERM.AI_RUN);
  const overviewLink = `/app/clients/${clientId}/overview`;
  const onboardingLink = `/app/clients/${clientId}/onboarding`;
  // Each readiness row maps a foundation requirement to a concrete
  // page anchor so the user can jump straight to the missing section.
  // A null readiness snapshot means the foundation calls hadn't
  // returned yet (or were permission-blocked); render the row unmarked
  // so we don't fake green checkmarks.
  //
  // Task 162: audience and goals are managed in the Onboarding wizard
  // (the strategy readiness gate is server-side in `enforceReadiness`,
  // page-agnostic — only the link targets moved). Brand identity and
  // the onboarding-profile compact card still live on Overview, so the
  // first two rows keep their `overviewLink` anchors.
  const checks: ReadonlyArray<{
    readonly key: string;
    readonly label: string;
    readonly hint: string;
    readonly href: string;
    readonly state: 'ok' | 'missing' | 'unknown';
  }> = readiness
    ? [
        {
          key: 'brand',
          label: 'هوية العلامة',
          hint: 'الاسم، الصناعة، الموقع، اللغة الأساسية',
          href: `${overviewLink}#brand-info-section`,
          state: readiness.hasBrandIdentity ? 'ok' : 'missing',
        },
        {
          key: 'profile',
          label: 'ملف التأهيل (الرؤية / الرسالة / القيم / USP)',
          hint: 'يفتح من تاب نظرة عامة → ملف التأهيل الكامل',
          href: `${overviewLink}#onboarding-profile-section`,
          state: readiness.hasOnboardingProfile ? 'ok' : 'missing',
        },
        {
          key: 'audience',
          label: `الجمهور المستهدف${readiness.audienceCount > 0 ? ` (${readiness.audienceCount})` : ''}`,
          hint: 'يلزم جمهور واحد على الأقل قبل بناء التموضع — يُحرَّر من ويزرد التأهيل',
          href: `${onboardingLink}#onboarding-section-market`,
          state: readiness.audienceCount > 0 ? 'ok' : 'missing',
        },
        {
          key: 'goals',
          label: `الأهداف${readiness.goalCount > 0 ? ` (${readiness.goalCount})` : ''}`,
          hint: 'يلزم هدف واحد على الأقل ليرتبط بالـ KPI — يُحرَّر من ويزرد التأهيل',
          href: `${onboardingLink}#onboarding-section-goals`,
          state: readiness.goalCount > 0 ? 'ok' : 'missing',
        },
      ]
    : [
        {
          key: 'brand',
          label: 'هوية العلامة',
          hint: '…',
          href: `${overviewLink}#brand-info-section`,
          state: 'unknown',
        },
        {
          key: 'profile',
          label: 'ملف التأهيل',
          hint: '…',
          href: `${overviewLink}#onboarding-profile-section`,
          state: 'unknown',
        },
        {
          key: 'audience',
          label: 'الجمهور المستهدف',
          hint: '…',
          href: `${onboardingLink}#onboarding-section-market`,
          state: 'unknown',
        },
        {
          key: 'goals',
          label: 'الأهداف',
          hint: '…',
          href: `${onboardingLink}#onboarding-section-goals`,
          state: 'unknown',
        },
      ];

  const okCount = checks.filter((c) => c.state === 'ok').length;
  const missingCount = checks.filter((c) => c.state === 'missing').length;
  const allReady = readiness !== null && missingCount === 0;

  return (
    <div className="card strategy-empty-card">
      <span className="meta">foundation strategy</span>
      <h2>الاستراتيجية الأساسية لم تُنشأ بعد</h2>
      <p>
        الاستراتيجية هنا ليست خدمة مدفوعة؛ هي أساس تشغيل المحتوى والحملات. بعد اكتمال التأهيل، تنشئ
        الوكالة نسخة استراتيجية قابلة للعرض والمراجعة بدل ترك العميل أمام صفحة فارغة.
      </p>

      <div className="onboarding-step-strip" aria-label="مسار الاستراتيجية">
        <span>01 / تأهيل العميل</span>
        <span>02 / تحليل الهوية والسوق</span>
        <span>03 / اعتماد الاستراتيجية</span>
        <span>04 / تشغيل المحتوى والحملات</span>
      </div>

      <div className="strategy-readiness-card">
        <div className="sec-title" style={{ marginBottom: 8 }}>
          <h3 style={{ fontSize: 14, margin: 0 }}>جاهزية البيانات قبل بناء الاستراتيجية</h3>
          <span className="meta">{readiness ? `${okCount}/${checks.length}` : '…'}</span>
        </div>
        <ul className="strategy-readiness-list">
          {checks.map((c) => (
            <li key={c.key} className={`strategy-readiness-item is-${c.state}`}>
              <span aria-hidden="true" className="strategy-readiness-mark">
                {c.state === 'ok' ? '✓' : c.state === 'missing' ? '○' : '·'}
              </span>
              <div>
                <Link to={c.href} className="strategy-readiness-label">
                  {c.label}
                </Link>
                <div className="tiny" style={{ color: 'var(--muted)', marginTop: 2 }}>
                  {c.hint}
                </div>
              </div>
            </li>
          ))}
        </ul>
        {readiness !== null ? (
          <p className="tiny" style={{ color: 'var(--muted)', marginTop: 10 }}>
            {allReady
              ? 'كل البيانات الأساسية جاهزة. اضغط الزر بالأسفل لإنشاء مسودة استراتيجية مولّدة من ملف التأهيل (وضع القالب).'
              : `ينقص ${missingCount} عنصر قبل أن تكون البيانات جاهزة لبناء الاستراتيجية.`}
          </p>
        ) : null}
      </div>

      <div className="strategy-empty-cta-row">
        {allReady && canGenerate ? (
          <button
            type="button"
            className="auth-cta"
            disabled={submitting || aiSubmitting}
            style={{ width: 'auto' }}
            onClick={() => {
              if (submitting || aiSubmitting) return;
              setSubmitting(true);
              setSubmitError(null);
              createClientStrategyFromOnboarding({ clientId })
                .then(() => {
                  // Refresh the list — the parent will switch out of
                  // the empty state once the new baseline lands.
                  onCreated();
                })
                .catch((err: unknown) => {
                  setSubmitting(false);
                  if (isApiError(err)) {
                    if (err.code === 'permission_denied') {
                      setSubmitError('لا تملك صلاحية إنشاء استراتيجية لهذا العميل.');
                      return;
                    }
                    if (err.code === 'conflict') {
                      setSubmitError(
                        'يوجد بالفعل استراتيجية أساسية فعّالة لهذا العميل. حدّث الصفحة لعرضها.',
                      );
                      return;
                    }
                    if (err.code === 'validation_error') {
                      // Task 274 — field-aware: only readiness keys become the
                      // "بيانات التأهيل ناقصة" copy. Non-readiness validation keys
                      // (_body / client_id / _rpc) get a neutral message instead of
                      // a mislabelled onboarding-missing banner leaking a raw key.
                      const fields = err.body.fields;
                      const labels = fields
                        ? Object.keys(fields)
                            .filter(isStrategyReadinessFieldKey)
                            .map(readinessFieldLabel)
                        : [];
                      setSubmitError(
                        labels.length > 0
                          ? `بيانات التأهيل ناقصة: ${labels.join('، ')}.`
                          : 'تعذّر إنشاء الاستراتيجية: تحقّق من صحة البيانات ثم حاول مجدداً.',
                      );
                      return;
                    }
                    if (err.code === 'not_found') {
                      setSubmitError('العميل غير متاح أو لا تملك وصولاً إليه.');
                      return;
                    }
                  }
                  setSubmitError('تعذّر إنشاء الاستراتيجية. حاول مجدداً.');
                });
            }}
          >
            {submitting ? <span className="spinner" /> : null}
            <span>{submitting ? 'جاري إنشاء الاستراتيجية…' : 'إنشاء من القالب'}</span>
          </button>
        ) : (
          <button
            type="button"
            className="btn-coming-soon"
            disabled
            title={
              !allReady
                ? 'أكمل البيانات الأساسية أولاً.'
                : 'تحتاج صلاحية strategy.create + strategy.version.publish لإنشاء الاستراتيجية.'
            }
          >
            إنشاء من القالب
          </button>
        )}
        {/* Task 061 + Task 085: AI generation alongside the template
            path. The AI version routes through `strategy.lead` agent →
            `strategy.approved_onboarding.generate` worker → harness
            (Task 056), and runs in a background task so the request
            returns under a second; the UI polls until success. */}
        {allReady && canAiGenerate ? (
          <button
            type="button"
            className="btn"
            disabled={submitting || aiSubmitting}
            style={{ width: 'auto' }}
            title="يولّد نسخة جديدة من بيانات التأهيل المعتمدة باستخدام النموذج (3-6 دقائق)"
            onClick={() => {
              if (submitting || aiSubmitting) return;
              setSubmitError(null);
              void aiJob.start();
            }}
          >
            {aiSubmitting ? <span className="spinner" /> : null}
            <span>
              {aiJob.phase.kind === 'starting'
                ? 'جاري بدء التوليد…'
                : aiJob.phase.kind === 'tracking'
                  ? 'جاري التوليد بالذكاء الاصطناعي…'
                  : 'توليد بالذكاء الاصطناعي'}
            </span>
          </button>
        ) : null}
        <Link
          to={`/app/clients/${clientId}/onboarding`}
          className="btn-text"
          style={{ padding: '8px 12px' }}
        >
          فتح ويزرد التأهيل ←
        </Link>
      </div>
      <p className="tiny" style={{ color: 'var(--muted)', marginTop: 10 }}>
        وضع القالب يولّد بنية ثابتة من ملف التأهيل بدون نموذج؛ وضع الذكاء الاصطناعي يستخدم بيانات
        التأهيل المعتمدة فقط لإنتاج محتوى متخصص — كلاهما يحتاج إلى مراجعة بشرية قبل الاعتماد
        النهائي.
      </p>
      <StrategyGenerationProgressPanel
        phase={aiJob.phase}
        onRetry={() => {
          setSubmitError(null);
          aiJob.reset();
          void aiJob.start();
        }}
      />
      {submitError ? (
        <div className="banner banner-error" role="alert" style={{ marginTop: 10 }}>
          {submitError}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Task 085 — shared progress + status panel for the async AI strategy
 * generation flow. Renders one of:
 *   - in-flight: Arabic copy + 3-6 minute hint + auto-update note,
 *   - failure: error banner with a retry button,
 *   - success: nothing (the parent's onCreated has already refreshed).
 */
function StrategyGenerationProgressPanel({
  phase,
  onRetry,
}: {
  readonly phase: import('./useStrategyGenerationJob').StrategyGenerationPhase;
  readonly onRetry: () => void;
}) {
  if (phase.kind === 'starting' || phase.kind === 'tracking') {
    return (
      <div className="banner banner-info" role="status" style={{ marginTop: 10 }}>
        <div>
          <strong>جاري تجهيز الاستراتيجية</strong>
        </div>
        <div className="tiny" style={{ marginTop: 4 }}>
          قد تستغرق العملية من 3 إلى 6 دقائق حسب حجم البيانات.
          <br />
          يمكنك البقاء في الصفحة وسيتم تحديث النتيجة تلقائياً.
        </div>
      </div>
    );
  }
  if (phase.kind === 'failure') {
    return (
      <div className="banner banner-error" role="alert" style={{ marginTop: 10 }}>
        <div>{phase.errorMessage}</div>
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn-text"
            onClick={onRetry}
            style={{ padding: '4px 10px', fontSize: 12 }}
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }
  return null;
}

function truthy(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Task 061: small AI-generate trigger used both inline in the
 * detail header (`variant="version"`, publishes a new version on
 * the existing strategy) and elsewhere if needed. Self-contained
 * state so adding it to other surfaces doesn't require parent
 * plumbing.
 */
function AiGenerateButton({
  clientId,
  onSaved,
  variant,
}: {
  readonly clientId: string;
  readonly onSaved: () => void;
  readonly variant: 'version';
}) {
  // Task 085: same async lifecycle as the empty-state button. The
  // detail-header surface uses the inline status panel below the
  // button instead of a separate progress card, since this component
  // is rendered next to the strategy title.
  const aiJob = useStrategyGenerationJob({
    clientId,
    onSuccess: onSaved,
  });
  const label =
    variant === 'version' ? 'توليد نسخة جديدة بالذكاء الاصطناعي' : 'توليد بالذكاء الاصطناعي';
  const buttonLabel =
    aiJob.phase.kind === 'starting'
      ? '…'
      : aiJob.phase.kind === 'tracking'
        ? 'جاري التوليد…'
        : label;
  return (
    <>
      <button
        type="button"
        className="btn-text"
        disabled={aiJob.isBusy}
        onClick={() => {
          if (aiJob.isBusy) return;
          void aiJob.start();
        }}
        style={{ padding: '6px 10px', fontSize: 12 }}
        title="يولّد نسخة استراتيجية جديدة من بيانات التأهيل المعتمدة (3-6 دقائق)"
      >
        {buttonLabel}
      </button>
      {aiJob.phase.kind === 'starting' || aiJob.phase.kind === 'tracking' ? (
        <span
          className="tiny"
          role="status"
          style={{ color: 'var(--muted)', marginInlineStart: 8 }}
        >
          جاري تجهيز الاستراتيجية — قد تستغرق العملية من 3 إلى 6 دقائق.
        </span>
      ) : null}
      {aiJob.phase.kind === 'failure' ? (
        <span className="tiny" role="alert" style={{ color: 'var(--rose)', marginInlineStart: 8 }}>
          {aiJob.phase.errorMessage}
          <button
            type="button"
            className="btn-text"
            onClick={() => {
              aiJob.reset();
              void aiJob.start();
            }}
            style={{ padding: '0 6px', fontSize: 12 }}
          >
            إعادة المحاولة
          </button>
        </span>
      ) : null}
    </>
  );
}

/**
 * Task 245 — "Create Content Batch" CTA.
 *
 * DELIBERATE, LOCALIZED deviation: this is the ONLY place in the app that calls
 * `supabase.rpc(...)` directly instead of routing through `kmiFetch` → an edge
 * function. Approved Option B: the RPC `create_content_batch_from_strategy_v1` is
 * already SECURITY DEFINER, granted to `authenticated`, and internally gated
 * (auth.uid / content.create / client assignment), so no edge function or deploy
 * is needed — it ships via Vercel on merge. Do NOT generalize this; every other
 * data call MUST keep using kmiFetch.
 *
 * No AI. Creates only DRAFT / manual content_items (one per strategy
 * content_pillar); no variants, schedule, client approval, or production task. The
 * backend is the source of truth — if it denies (403/42501), we render a clean
 * message rather than trusting the FE gate.
 */
interface ContentBatchRow {
  readonly batch_id: string;
  readonly content_item_id: string;
  readonly pillar_name: string;
}

type ContentBatchState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'submitting' }
  | {
      readonly kind: 'success';
      readonly batchId: string;
      readonly rows: ReadonlyArray<ContentBatchRow>;
    }
  | { readonly kind: 'error'; readonly message: string };

function CreateContentBatchButton({
  clientId,
  strategyId,
}: {
  readonly clientId: string;
  readonly strategyId: string;
}) {
  const [state, setState] = useState<ContentBatchState>({ kind: 'idle' });

  async function run(): Promise<void> {
    if (state.kind === 'submitting') return;
    setState({ kind: 'submitting' });
    const { data, error } = await supabase.rpc('create_content_batch_from_strategy_v1', {
      p_client_id: clientId,
      p_strategy_id: strategyId,
      p_title: null,
    });
    if (error) {
      // Backend is the source of truth — map its gate SQLSTATEs to clean copy.
      const code = (error as { code?: string }).code;
      const message =
        code === '42501'
          ? 'لا تملك صلاحية إنشاء دفعة محتوى من هذه الاستراتيجية.'
          : code === '23503'
            ? 'الاستراتيجية أو العميل غير متاح.'
            : code === '22023'
              ? 'الاستراتيجية غير جاهزة لإنشاء دفعة — تأكد من وجود نسخة نشطة وأعمدة محتوى.'
              : 'تعذّر إنشاء الدفعة. حاول مجدداً.';
      setState({ kind: 'error', message });
      return;
    }
    const rows = (data ?? []) as ReadonlyArray<ContentBatchRow>;
    if (rows.length === 0 || !rows[0]) {
      setState({ kind: 'error', message: 'لم يتم إنشاء أي مسودة من الاستراتيجية.' });
      return;
    }
    // Deliberately NO parent refetch here. The batch creates draft content_items
    // (surfaced on the Content tab), and nothing on this strategy board changes — so
    // a refetch would only remount this component and discard the success summary the
    // user needs to read (batch id, count, titles, "view drafts" link). The link
    // navigates to the drafts on demand. Do NOT add onSaved() back.
    setState({ kind: 'success', batchId: rows[0].batch_id, rows });
  }

  return (
    <>
      <button
        type="button"
        className="btn-text"
        disabled={state.kind === 'submitting'}
        onClick={() => {
          void run();
        }}
        style={{ padding: '6px 10px', fontSize: 12 }}
        title="ينشئ مسودات محتوى من أعمدة الاستراتيجية المختارة (بدون ذكاء اصطناعي)"
      >
        {state.kind === 'submitting' ? 'جاري الإنشاء…' : 'إنشاء دفعة محتوى'}
      </button>
      {state.kind === 'success' ? (
        <span
          className="tiny"
          role="status"
          style={{ color: 'var(--muted)', marginInlineStart: 8 }}
        >
          تم إنشاء دفعة محتوى ({state.batchId.slice(0, 8)}) — {state.rows.length} مسودة:{' '}
          {state.rows.map((r) => r.pillar_name).join('، ')}.{' '}
          <Link to="../content" className="btn-text" style={{ padding: '0 6px', fontSize: 12 }}>
            عرض المسودات في المحتوى
          </Link>
        </span>
      ) : null}
      {state.kind === 'error' ? (
        <span className="tiny" role="alert" style={{ color: 'var(--rose)', marginInlineStart: 8 }}>
          {state.message}
        </span>
      ) : null}
    </>
  );
}

function StrategyDetail({
  state,
  editing,
  canPublishVersion,
  canAiRun,
  canCreateContentBatch,
  canViewStrategyRules,
  canManageStrategyRules,
  onEdit,
  onCancelEdit,
  onSaved,
}: {
  readonly state: DetailState;
  readonly editing: boolean;
  readonly canPublishVersion: boolean;
  readonly canAiRun: boolean;
  readonly canCreateContentBatch: boolean;
  readonly canViewStrategyRules: boolean;
  readonly canManageStrategyRules: boolean;
  readonly onEdit: () => void;
  readonly onCancelEdit: () => void;
  readonly onSaved: () => void;
}) {
  if (state.status === 'idle')
    return (
      <EmptyState
        title="اختر استراتيجية لعرض التفاصيل"
        description="اضغط أي بطاقة من القائمة على اليمين."
      />
    );
  if (state.status === 'loading') return <LoadingState title="جاري تحميل التفاصيل…" />;
  if (state.status === 'not-found')
    return <NotFoundState title="الاستراتيجية غير متاحة" description="ربما تم أرشفتها." />;
  if (state.status === 'error')
    return <ErrorState title="تعذر تحميل تفاصيل الاستراتيجية" error={state.error} />;

  const { strategy, active_version: activeVersion } = state.detail;

  // Edit mode replaces the document view — but only when there's an
  // active version to edit. The empty-version branch falls through to
  // the existing "no active version" empty state so the editor is
  // never opened against an inert strategy.
  if (editing && activeVersion && strategy.is_active) {
    return (
      <StrategyEditor
        strategyId={strategy.id}
        currentContent={activeVersion.content}
        currentSummary={activeVersion.summary}
        currentVersionId={activeVersion.id}
        currentVersionNumber={activeVersion.version}
        onCancel={onCancelEdit}
        onSaved={onSaved}
        canAiRefine={canAiRun && canPublishVersion}
      />
    );
  }

  return (
    <div className="stack-md">
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>{strategy.title}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="meta">strategy</span>
            {/*
              Edit CTA — only surfaced for agency users with the
              `strategy.version.publish` permission, only when the
              strategy is active, and only when there's an active
              version to base the edit on. Backend re-checks the perm
              regardless. Hidden for client-tier viewers and for
              archived strategies.
            */}
            {canPublishVersion && strategy.is_active && activeVersion ? (
              <>
                <button
                  type="button"
                  className="btn-text"
                  onClick={onEdit}
                  style={{ padding: '6px 10px', fontSize: 12 }}
                >
                  تعديل الاستراتيجية
                </button>
                {/* Task 061: AI-generate a fresh immutable version
                    from the approved canonical onboarding. Same
                    publish-version RPC under the hood; the AI just
                    supplies the content. Requires `ai.run`; the
                    backend correctly 403s if missing, this just
                    keeps the UX honest. */}
                {canAiRun ? (
                  <AiGenerateButton
                    clientId={strategy.client_id}
                    onSaved={onSaved}
                    variant="version"
                  />
                ) : null}
              </>
            ) : null}
            {/* Task 245 — "Create Content Batch" CTA. A SEPARATE gate from edit/AI:
                surfaced for content.create holders (owner / marketing_manager by
                default) when the strategy is active with an active version. The
                backend RPC re-checks; a clean denial renders if it 403s. */}
            {canCreateContentBatch && strategy.is_active && activeVersion ? (
              <CreateContentBatchButton clientId={strategy.client_id} strategyId={strategy.id} />
            ) : null}
          </div>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            gap: 14,
          }}
        >
          <Field label="الحالة" value={strategy.is_active ? 'نشطة' : 'متوقفة'} />
          <Field label="نوع" value={strategy.is_baseline ? 'أساسية' : 'فرعية'} />
          <Field label="آخر تحديث" value={new Date(strategy.updated_at).toLocaleString('ar-SA')} />
          <Field label="معرف العميل" value={strategy.client_id} mono />
        </div>
      </div>

      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>الإصدار الفعّال</h2>
          <span className="meta">strategy_versions.active_version_id</span>
        </div>
        {activeVersion ? (
          <ActiveVersionBlock version={activeVersion} />
        ) : (
          <EmptyState
            title="لا يوجد إصدار فعّال"
            description="ستظهر هنا تفاصيل آخر إصدار معتمد عند توفّره."
          />
        )}
      </div>

      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>اقتراحات الاستراتيجية</h2>
          <span className="meta">
            {state.suggestions.length} {state.suggestionsError ? '· غير متاح' : ''}
          </span>
        </div>
        <SuggestionsBlock
          suggestions={state.suggestions}
          suggestionsError={state.suggestionsError}
        />
      </div>

      {/* Task 259 — Strategy Rules are READ-ONLY here (status + link). The
          derive/review surface lives in the Creative Intelligence workflow
          (stage 5, after Analysis), per product UX. VIEW stays gated on
          strategy.rule.review. */}
      {canViewStrategyRules ? (
        <StrategyRulesPanel
          clientId={strategy.client_id}
          strategyId={strategy.id}
          variant="readonly"
          canManage={canManageStrategyRules}
          canDerive={canManageStrategyRules && strategy.is_active && !!activeVersion}
          manageHref={`/app/clients/${strategy.client_id}/creative-intelligence`}
        />
      ) : null}
    </div>
  );
}

function ActiveVersionBlock({
  version,
}: {
  readonly version: NonNullable<GetClientStrategyData['active_version']>;
}) {
  const render = useMemo(() => normalizeStrategyContent(version.content), [version.content]);
  const hasAny = hasAnyV1Section(render);
  const hasContent = Object.keys(version.content).length > 0;
  return (
    <div className="stack-sm">
      <div className="strategy-document-hero">
        <div>
          <span className="meta">الإصدار {version.version}</span>
          <h3>ملخص الاستراتيجية</h3>
          <p>{version.summary || 'لا يوجد ملخص مكتوب لهذا الإصدار.'}</p>
        </div>
        <div className="strategy-document-stamp">
          <span>تاريخ الإنشاء</span>
          <strong>{new Date(version.created_at).toLocaleDateString('ar-SA')}</strong>
          <small>{version.ai_run_id ? 'مرتبط بتشغيل AI' : 'إصدار يدوي'}</small>
        </div>
      </div>
      {hasContent ? (
        <>
          {hasAny ? (
            <StrategyDocumentView render={render} />
          ) : (
            <p className="muted" style={{ margin: 0 }}>
              لم يتطابق محتوى هذا الإصدار مع عقد الاستراتيجية V1. يمكن التدقيق أدناه على البيانات
              الخام.
            </p>
          )}
          {/*
            Task 088 Phase 0 — both the user-facing "أقسام خارج عقد V1"
            block AND the raw-JSON `<details>` dump are removed. They
            leaked internal V2 pipeline fields (brand_strategy_core_v2,
            source_metadata, warnings, placeholders, confidence_score,
            model id, tokens, cost) into a marketing-team UI. The
            renderer's V0 normalizer is the source of the document view;
            anything outside its vocabulary is silently dropped. If
            operators need the raw row, they query
            `strategy_versions.content` directly.
          */}
        </>
      ) : null}
    </div>
  );
}

function StrategyDocumentView({ render }: { readonly render: StrategyContentV1Render }) {
  return (
    <div className="strategy-document">
      {render.positioning ? <PositioningCard data={render.positioning} /> : null}
      {render.value_proposition ? <ValuePropositionCard data={render.value_proposition} /> : null}
      {render.brand_values.length > 0 ? (
        <DocumentCard tone="strategy-card-mint" eyebrow="brand_values" title="القيم الأساسية">
          <ul className="strategy-bullets">
            {render.brand_values.map((v, i) => (
              <li key={`${v.name}-${i}`}>
                <strong>{v.name}</strong>
                {v.description ? <span className="muted"> · {v.description}</span> : null}
              </li>
            ))}
          </ul>
        </DocumentCard>
      ) : null}
      {render.target_personas.length > 0 ? <PersonasCard items={render.target_personas} /> : null}
      {render.competitor_summary.length > 0 ? (
        <CompetitorSummaryCard items={render.competitor_summary} />
      ) : null}
      {render.content_pillars.length > 0 ? (
        <ContentPillarsCard items={render.content_pillars} />
      ) : null}
      {render.tone_guidelines ? <ToneGuidelinesCard data={render.tone_guidelines} /> : null}
      {render.channel_recommendations.length > 0 ? (
        <ChannelsCard items={render.channel_recommendations} />
      ) : null}
      {render.monthly_content_mix.length > 0 ? (
        <ContentMixCard items={render.monthly_content_mix} />
      ) : null}
      {render.roadmap.length > 0 ? <RoadmapCard items={render.roadmap} /> : null}
      {render.kpi_targets.length > 0 ? <KpiTargetsCard items={render.kpi_targets} /> : null}
      {render.dos_donts ? <DosDontsCard data={render.dos_donts} /> : null}
    </div>
  );
}

function DocumentCard({
  tone,
  eyebrow,
  title,
  children,
}: {
  readonly tone: string;
  readonly eyebrow: string;
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <article className={`strategy-content-card ${tone}`}>
      <span className="meta">{eyebrow}</span>
      <h3>{title}</h3>
      {children}
    </article>
  );
}

function PositioningCard({ data }: { readonly data: PositioningSection }) {
  return (
    <DocumentCard tone="strategy-card-mint" eyebrow="positioning" title="الموقع والتموضع">
      {data.statement ? <p style={{ margin: '0 0 8px' }}>{data.statement}</p> : null}
      {data.audience || data.alternative ? (
        <div className="strategy-kv-list">
          {data.audience ? (
            <div>
              <strong>الجمهور</strong>
              <span>{data.audience}</span>
            </div>
          ) : null}
          {data.alternative ? (
            <div>
              <strong>البديل المرفوض</strong>
              <span>{data.alternative}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      {!data.statement && !data.audience && !data.alternative ? (
        <p className="muted">لم يُسجّل بيان تموضع.</p>
      ) : null}
    </DocumentCard>
  );
}

function ValuePropositionCard({ data }: { readonly data: ValuePropositionSection }) {
  return (
    <DocumentCard tone="strategy-card-butter" eyebrow="value_proposition" title="عرض القيمة">
      {data.headline ? <p style={{ margin: '0 0 6px', fontWeight: 600 }}>{data.headline}</p> : null}
      {data.body ? <p style={{ margin: '0 0 8px' }}>{data.body}</p> : null}
      {data.proof.length > 0 ? (
        <ul className="strategy-bullets">
          {data.proof.map((p, i) => (
            <li key={`proof-${i}`}>{p}</li>
          ))}
        </ul>
      ) : null}
      {!data.headline && !data.body && data.proof.length === 0 ? (
        <p className="muted">لم يُسجّل عرض قيمة.</p>
      ) : null}
    </DocumentCard>
  );
}

function PersonasCard({ items }: { readonly items: ReadonlyArray<PersonaItem> }) {
  return (
    <DocumentCard tone="strategy-card-sky" eyebrow="target_personas" title="شخصيات الجمهور">
      <div className="strategy-personas">
        {items.map((p, i) => (
          <div key={`${p.name}-${i}`} className="strategy-persona">
            <strong>{p.name}</strong>
            {p.demographic ? <span className="tiny muted">{p.demographic}</span> : null}
            {p.psychographic ? <p style={{ margin: '4px 0 0' }}>{p.psychographic}</p> : null}
            {p.jobs_to_be_done.length > 0 ? (
              <div style={{ marginTop: 6 }}>
                <span className="lbl">المهام</span>
                <ul className="strategy-bullets">
                  {p.jobs_to_be_done.map((j, k) => (
                    <li key={`jtbd-${k}`}>{j}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {p.pain_points.length > 0 ? (
              <div style={{ marginTop: 6 }}>
                <span className="lbl">نقاط الألم</span>
                <ul className="strategy-bullets">
                  {p.pain_points.map((j, k) => (
                    <li key={`pain-${k}`}>{j}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </DocumentCard>
  );
}

function CompetitorSummaryCard({
  items,
}: {
  readonly items: ReadonlyArray<CompetitorSummaryItem>;
}) {
  return (
    <DocumentCard tone="strategy-card-rose" eyebrow="competitor_summary" title="ملخص المنافسين">
      <ul className="strategy-bullets">
        {items.map((c, i) => (
          <li key={`${c.name}-${i}`}>
            <strong>{c.name}</strong>
            {c.positioning ? <span className="muted"> · {c.positioning}</span> : null}
            {c.strength ? <span className="tiny"> · قوة: {c.strength}</span> : null}
            {c.weakness ? <span className="tiny"> · ضعف: {c.weakness}</span> : null}
          </li>
        ))}
      </ul>
    </DocumentCard>
  );
}

function ContentPillarsCard({ items }: { readonly items: ReadonlyArray<ContentPillarItem> }) {
  return (
    <DocumentCard tone="strategy-card-paper" eyebrow="content_pillars" title="أعمدة المحتوى">
      <ul className="strategy-bullets">
        {items.map((p, i) => (
          <li key={`${p.name}-${i}`}>
            <strong>{p.name}</strong>
            {p.description ? <span className="muted"> · {p.description}</span> : null}
            {p.cadence ? <span className="tiny"> · {p.cadence}</span> : null}
          </li>
        ))}
      </ul>
    </DocumentCard>
  );
}

function ToneGuidelinesCard({ data }: { readonly data: ToneGuidelinesSection }) {
  return (
    <DocumentCard tone="strategy-card-paper" eyebrow="tone_guidelines" title="نبرة التواصل">
      {data.voice ? (
        <p style={{ margin: '0 0 8px' }}>
          <strong>الصوت: </strong>
          {data.voice}
        </p>
      ) : null}
      {data.tone.length > 0 ? (
        <p style={{ margin: '0 0 8px' }}>
          <strong>النبرة: </strong>
          {data.tone.join('، ')}
        </p>
      ) : null}
      {data.dos.length > 0 || data.donts.length > 0 ? (
        <div className="strategy-do-dont">
          <div>
            <span className="lbl">افعل</span>
            <ul className="strategy-bullets">
              {data.dos.map((d, i) => (
                <li key={`do-${i}`}>{d}</li>
              ))}
            </ul>
          </div>
          <div>
            <span className="lbl">تجنّب</span>
            <ul className="strategy-bullets">
              {data.donts.map((d, i) => (
                <li key={`dont-${i}`}>{d}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </DocumentCard>
  );
}

function ChannelsCard({ items }: { readonly items: ReadonlyArray<ChannelRecommendationItem> }) {
  return (
    <DocumentCard tone="strategy-card-sky" eyebrow="channel_recommendations" title="القنوات">
      <ul className="strategy-bullets">
        {items.map((c, i) => (
          <li key={`${c.channel}-${i}`}>
            <strong>{c.channel}</strong>
            {c.role ? <span className="muted"> · {c.role}</span> : null}
            {c.cadence ? <span className="tiny"> · {c.cadence}</span> : null}
            {c.kpis.length > 0 ? <span className="tiny"> · KPIs: {c.kpis.join('، ')}</span> : null}
          </li>
        ))}
      </ul>
    </DocumentCard>
  );
}

function ContentMixCard({ items }: { readonly items: ReadonlyArray<ContentMixEntry> }) {
  return (
    <DocumentCard
      tone="strategy-card-butter"
      eyebrow="monthly_content_mix"
      title="مزيج المحتوى الشهري"
    >
      <ul className="strategy-bullets">
        {items.map((m, i) => (
          <li key={`${m.pillar}-${i}`}>
            <strong>{m.pillar}</strong>
            {typeof m.percent === 'number' ? <span> · {m.percent}%</span> : null}
            {m.raw && typeof m.percent !== 'number' ? (
              <span className="muted"> · {m.raw}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </DocumentCard>
  );
}

function RoadmapCard({ items }: { readonly items: ReadonlyArray<RoadmapItem> }) {
  return (
    <DocumentCard tone="strategy-card-mint" eyebrow="roadmap" title="خارطة التنفيذ">
      <ol className="strategy-roadmap">
        {items.map((r, i) => (
          <li key={`${r.phase}-${i}`}>
            <strong>{r.phase}</strong>
            {r.period ? <span className="tiny muted"> · {r.period}</span> : null}
            {r.focus ? <p style={{ margin: '4px 0 0' }}>{r.focus}</p> : null}
            {r.deliverables.length > 0 ? (
              <ul className="strategy-bullets">
                {r.deliverables.map((d, k) => (
                  <li key={`del-${k}`}>{d}</li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ol>
    </DocumentCard>
  );
}

function KpiTargetsCard({ items }: { readonly items: ReadonlyArray<KpiTargetItem> }) {
  return (
    <DocumentCard tone="strategy-card-sky" eyebrow="kpi_targets" title="مؤشرات الأداء المستهدفة">
      <ul className="strategy-bullets">
        {items.map((k, i) => (
          <li key={`${k.metric}-${i}`}>
            <strong>{k.metric}</strong>
            {k.target ? <span> · {k.target}</span> : null}
            {k.period ? <span className="tiny muted"> · {k.period}</span> : null}
          </li>
        ))}
      </ul>
    </DocumentCard>
  );
}

function DosDontsCard({ data }: { readonly data: DosDontsSection }) {
  return (
    <DocumentCard tone="strategy-card-rose" eyebrow="dos_donts" title="افعل / تجنّب">
      <div className="strategy-do-dont">
        <div>
          <span className="lbl">افعل</span>
          {data.dos.length === 0 ? (
            <p className="muted">لا يوجد.</p>
          ) : (
            <ul className="strategy-bullets">
              {data.dos.map((d, i) => (
                <li key={`do-${i}`}>{d}</li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <span className="lbl">تجنّب</span>
          {data.donts.length === 0 ? (
            <p className="muted">لا يوجد.</p>
          ) : (
            <ul className="strategy-bullets">
              {data.donts.map((d, i) => (
                <li key={`dont-${i}`}>{d}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DocumentCard>
  );
}

// Task 088 Phase 0: UnknownSectionsBlock + previewValue removed.
// They rendered an "أقسام خارج عقد V1" debug surface plus a raw JSON
// dump that leaked V2 pipeline internals (brand_strategy_core_v2,
// source_metadata, warnings, placeholders, confidence_score, model id,
// tokens, cost) into a marketing-team UI. Kept the source-truth surface
// (StrategyDocumentView) intact; everything outside its V0 vocabulary
// is silently dropped now.

function SuggestionsBlock({
  suggestions,
  suggestionsError,
}: {
  readonly suggestions: ReadonlyArray<StrategySuggestionRow>;
  readonly suggestionsError: unknown | null;
}) {
  if (suggestionsError) {
    if (isApiError(suggestionsError) && suggestionsError.code === 'permission_denied') {
      return (
        <p className="body">
          الاقتراحات متاحة لمستخدمي الوكالة فقط. حسابك لا يملك الصلاحية المطلوبة.
        </p>
      );
    }
    if (isApiError(suggestionsError) && suggestionsError.code === 'not_found') {
      return (
        <EmptyState
          title="لا توجد اقتراحات"
          description="ستظهر هنا اقتراحات الاستراتيجية عند توفّرها."
        />
      );
    }
    return (
      <p className="tiny" style={{ color: 'var(--muted)' }}>
        تعذر تحميل الاقتراحات.
      </p>
    );
  }
  if (suggestions.length === 0) {
    return (
      <EmptyState
        title="لا توجد اقتراحات"
        description="ستظهر هنا اقتراحات الاستراتيجية الآلية أو اليدوية."
      />
    );
  }
  return (
    <ul className="row-list">
      {suggestions.map((s) => (
        <li key={s.id} className="row-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <strong style={{ fontSize: 13 }}>{s.title}</strong>
            <span className={`pill pill-${suggestionPillVariant(s.status)}`}>
              {suggestionStatusLabel(s.status)}
            </span>
          </div>
          {s.body ? (
            <p className="body" style={{ marginTop: 6 }}>
              {s.body}
            </p>
          ) : null}
          <div className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
            {s.source ?? 'بدون مصدر'} · {new Date(s.created_at).toLocaleDateString('ar-SA')}
          </div>
        </li>
      ))}
    </ul>
  );
}

function suggestionStatusLabel(status: StrategySuggestionRow['status']): string {
  switch (status) {
    case 'pending':
      return 'قيد المراجعة';
    case 'accepted':
      return 'مقبولة';
    case 'rejected':
      return 'مرفوضة';
    case 'dismissed':
      return 'مهملة';
    default:
      return status;
  }
}

function suggestionPillVariant(status: StrategySuggestionRow['status']): string {
  switch (status) {
    case 'pending':
      return 'review';
    case 'accepted':
      return 'active';
    case 'rejected':
      return 'error';
    case 'dismissed':
      return 'paused';
    default:
      return 'info';
  }
}

function Field({
  label,
  value,
  mono,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
  readonly mono?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <span className="body" style={mono ? { fontFamily: 'var(--font-mono)', fontSize: 11 } : {}}>
        {value && value.length > 0 ? value : '—'}
      </span>
    </div>
  );
}
